import { getApiErrorContext } from './api.js';

/**
 * A SERVER REFUSAL THAT NAMES A FIELD LANDS ON THAT FIELD (§3.12).
 *
 * The outcome of a write is a toast — but "Enter a valid email" belongs next to
 * the email box, not in a toast that fades while the reader hunts for which of
 * twelve inputs was wrong. This reads the field list out of a refusal so a form
 * can put each message where it belongs, and the toast only says that some
 * fields need attention.
 *
 * Two shapes reach the client, and the old reader handled neither correctly:
 *
 *  - the zod middleware sends `details: [{ path: 'email', message }]` — an
 *    ARRAY whose `path` is a dotted STRING (the server joins it). Indexing that
 *    string with `[0]` keyed the error under `'e'`, which no form has.
 *  - a hand-built `ApiError.validation(msg, { fields: [{ path: [...], message }] })`
 *    nests the list under `fields`, with `path` as an array.
 *
 * Every issue is recorded under its full dotted path AND its first segment, so
 * a form keyed by top-level field (`email`) and one keyed by nested path
 * (`address.city`) both find it. The first message per key wins.
 */
export type ServerFieldErrors = Record<string, string>;

interface Issue {
  path: string;
  message: string;
}

function asIssue(value: unknown): Issue | null {
  if (value === null || typeof value !== 'object') return null;
  const rec = value as Record<string, unknown>;
  const message = typeof rec['message'] === 'string' ? rec['message'] : '';
  const raw = rec['path'];
  const path = Array.isArray(raw)
    ? raw.map((p) => String(p)).join('.')
    : typeof raw === 'string' || typeof raw === 'number'
      ? String(raw)
      : '';
  return path && message ? { path, message } : null;
}

/** The raw issue list out of a refusal (or a bare issue array), or `[]`. */
export function serverIssues(errOrDetails: unknown): Issue[] {
  let list: unknown = errOrDetails;
  if (!Array.isArray(errOrDetails)) {
    const ctx = getApiErrorContext(errOrDetails);
    const details = ctx.details as unknown;
    list = Array.isArray(details)
      ? details
      : details && typeof details === 'object'
        ? (details as Record<string, unknown>)['fields']
        : undefined;
    // A plain response-shaped object, an ApiErrorPayload, or `{ details }`.
    if (!Array.isArray(list) && errOrDetails && typeof errOrDetails === 'object') {
      const obj = errOrDetails as {
        response?: { data?: { error?: { details?: unknown } } };
        error?: { details?: unknown };
        details?: unknown;
      };
      list = obj.response?.data?.error?.details ?? obj.error?.details ?? obj.details;
    }
  }
  if (!Array.isArray(list)) return [];
  return list.map(asIssue).filter((i): i is Issue => i !== null);
}

/** `{ 'email': 'Invalid email', 'address.city': 'Required', 'address': 'Required' }`. */
export function serverFieldErrors(errOrDetails: unknown): ServerFieldErrors {
  const out: ServerFieldErrors = {};
  for (const { path, message } of serverIssues(errOrDetails)) {
    if (!(path in out)) out[path] = message;
    const head = path.split('.')[0] ?? path;
    if (head && !(head in out)) out[head] = message;
  }
  return out;
}
